import React from 'react';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaHeart, FaLightbulb, FaUsers, FaSeedling, FaChartLine, FaTrophy, FaQuoteLeft } from 'react-icons/fa';
import { useSchool } from '../contexts/SchoolContext';
import './About.css';

const About = () => {
  const { settings } = useSchool();
  const statistics = settings.statistics ?? [];
  return (
    <div className="about">
      <section className="about-hero page-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            About KSMS
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Building strong foundations for lifelong learning and character
          </motion.p>
        </div>
      </section>

      <section className="section legacy-section">
        <div className="container">
          <motion.div
            className="about-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="about-text">
              <h2>A Legacy of Excellence: The Journey of Our School</h2>
              <p>
                Founded in <strong>August 2011</strong>, our institution is the realization of a shared 
                vision held by dedicated educationists <strong>Mrs. Khadija Sarfraz</strong> and{' '}
                <strong>Mr. Sarfraz Ahmed</strong>. What began as a passionate commitment to 
                foundational learning has evolved into a cornerstone of academic excellence 
                within our community.
              </p>
            </div>
          </motion.div>

          <div className="legacy-timeline">
            <motion.div
              className="timeline-item"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="timeline-icon">
                <FaSeedling />
              </div>
              <div className="timeline-content">
                <h3>Our Humble Beginnings</h3>
                <p>
                  The story of our school started with a single, vital focus: the{' '}
                  <strong>Montessori level</strong>. Recognizing that the early years are the most 
                  transformative in a child's life, our founders worked tirelessly to create a 
                  nurturing environment where curiosity was encouraged and character was built. 
                  Through their unwavering hard work and personal investment in every student's 
                  success, the school quickly earned a reputation for quality and care.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="timeline-item"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="timeline-icon">
                <FaChartLine />
              </div>
              <div className="timeline-content">
                <h3>Growth Rooted in Trust</h3>
                <p>
                  As our students grew, so did we. Driven by the trust of our parent community 
                  and the academic milestones of our learners, the school underwent a{' '}
                  <strong>gradual and organic expansion</strong>.
                </p>
                <ul className="growth-milestones">
                  <li>
                    <strong>Foundation:</strong> Established the Montessori and Playgroup programs 
                    with a focus on holistic development.
                  </li>
                  <li>
                    <strong>Progression:</strong> Year by year, we introduced primary and middle 
                    school levels, ensuring our high standards remained consistent.
                  </li>
                  <li>
                    <strong>Achievement:</strong> Today, we are proud to see our inaugural batches 
                    reach <strong>Grade 10</strong>, completing a full circle of education from their 
                    first steps in a classroom to their secondary school matriculation.
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              className="timeline-item"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="timeline-icon">
                <FaTrophy />
              </div>
              <div className="timeline-content">
                <h3>A Vision for the Future</h3>
                <p>
                  For over a decade, Mrs. Khadija Sarfraz and Mr. Sarfraz Ahmed have remained 
                  at the helm, ensuring that the school's original ethos—<strong>academic rigor 
                  paired with empathetic mentorship</strong>—continues to guide every lesson.
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="legacy-quote"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <FaQuoteLeft className="quote-icon" />
            <blockquote>
              Our journey is not just measured in years, but in the success stories of the 
              students who have grown with us from playgroup to graduation.
            </blockquote>
          </motion.div>

          <motion.p
            className="legacy-closing"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            We invite you to be a part of our ongoing story as we continue to shape the 
            leaders, thinkers, and innovators of tomorrow.
          </motion.p>
        </div>
      </section>

      <section className="section values-section">
        <div className="container">
          <h2 className="section-title">Our Core Values</h2>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <FaGraduationCap />
              </div>
              <h3>Excellence</h3>
              <p>
                We strive for excellence in all aspects of education, from curriculum 
                design to teaching practices, ensuring every child receives the highest 
                quality education.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <FaHeart />
              </div>
              <h3>Compassion</h3>
              <p>
                We foster a caring, supportive community where children feel safe, 
                valued, and encouraged to take risks and learn from their experiences.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <FaLightbulb />
              </div>
              <h3>Innovation</h3>
              <p>
                We embrace innovative teaching methods and technologies that enhance 
                learning while staying true to time-tested educational principles.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <FaUsers />
              </div>
              <h3>Community</h3>
              <p>
                We believe in building strong partnerships with families and fostering 
                a sense of community that extends beyond the classroom.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section mission-section">
        <div className="container">
          <div className="mission-vision">
            <div className="mission-card">
              <h2>Our Mission</h2>
              <p>
                To provide a nurturing, child-centered educational environment that 
                fosters intellectual curiosity, emotional intelligence, and social 
                responsibility. We are committed to developing confident, compassionate, 
                and capable learners who are prepared to thrive in an ever-changing world.
              </p>
            </div>

            <div className="mission-card">
              <h2>Our Vision</h2>
              <p>
                To be a leading educational institution recognized for excellence in 
                Montessori and Secondary education, where every child discovers their 
                potential, develops a love for learning, and grows into a well-rounded 
                individual ready to make positive contributions to society.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Figures come from School Settings (school-confirmed); the section is hidden when there are none. */}
      {statistics.length > 0 && (
        <section className="section stats-section" aria-label="The school in numbers">
          <div className="container">
            <div className="stats-grid">
              {statistics.map((s) => (
                <div className="stat-card" key={`${s.value}-${s.label}`}>
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default About;
